// =====================================================
// SESSION FORM DIALOG
// Create / Edit event sessions
// =====================================================

import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useSessionMutations } from '../hooks/useEventSessions';
import type { EventSession } from '@/types/event.types';

// ===================
// TYPES
// ===================

interface SessionFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  /** If provided, form is in edit mode */
  session?: EventSession | null;
  /** Constrains selectable dates to the event range */
  eventStartDate?: string;
  eventEndDate?: string;
}

interface FormState {
  name: string;
  description: string;
  session_date: string;
  start_time: string;
  end_time: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  session_date: '',
  start_time: '',
  end_time: '',
};

// ===================
// COMPONENT
// ===================

export function SessionFormDialog({
  open,
  onOpenChange,
  eventId,
  session,
  eventStartDate,
  eventEndDate,
}: SessionFormDialogProps) {
  const isEditing = !!session;
  const { createSession, updateSession, isCreating, isUpdating } = useSessionMutations(eventId);
  const isProcessing = isCreating || isUpdating;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<FormState>>({});

  // Populate form when editing or reset when creating
  useEffect(() => {
    if (open) {
      if (session) {
        setForm({
          name: session.name,
          description: session.description ?? '',
          session_date: session.session_date,
          start_time: session.start_time ? session.start_time.slice(0, 5) : '',
          end_time: session.end_time ? session.end_time.slice(0, 5) : '',
        });
      } else {
        // New session: default to the event's first day
        setForm({ ...EMPTY_FORM, session_date: eventStartDate ?? '' });
      }
      setErrors({});
    }
  }, [open, session, eventStartDate]);

  const validate = (): boolean => {
    const newErrors: Partial<FormState> = {};

    if (!form.name.trim()) newErrors.name = 'El nombre es requerido';
    if (!form.session_date) newErrors.session_date = 'La fecha es requerida';
    if (form.start_time && form.end_time && form.end_time <= form.start_time) {
      newErrors.end_time = 'La hora de fin debe ser posterior a la hora de inicio';
    }
    if (form.start_time && !form.end_time) {
      newErrors.end_time = 'Debe indicar la hora de fin si hay hora de inicio';
    }
    if (!form.start_time && form.end_time) {
      newErrors.start_time = 'Debe indicar la hora de inicio si hay hora de fin';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const sessionData = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      session_date: form.session_date,
      start_time: form.start_time || null,
      end_time: form.end_time || null,
    };

    try {
      if (isEditing) {
        await updateSession({ id: session.id, data: sessionData });
      } else {
        await createSession({ ...sessionData, event_id: eventId });
      }
      onOpenChange(false);
    } catch {
      // error toast handled by mutation
    }
  };

  const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Sesión' : 'Nueva Sesión'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Modifica los datos de la sesión' : 'Agrega una nueva sesión al evento'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Name */}
          <div className="space-y-1">
            <Label htmlFor="session-name">
              Nombre <span className="text-destructive">*</span>
            </Label>
            <Input
              id="session-name"
              value={form.name}
              onChange={set('name')}
              placeholder="Ej: Ceremonia de Apertura"
              disabled={isProcessing}
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>

          {/* Description */}
          <div className="space-y-1">
            <Label htmlFor="session-description">Descripción (opcional)</Label>
            <Textarea
              id="session-description"
              value={form.description}
              onChange={set('description')}
              placeholder="Detalles adicionales sobre esta sesión..."
              rows={2}
              disabled={isProcessing}
            />
          </div>

          {/* Date */}
          <div className="space-y-1">
            <Label htmlFor="session-date">
              Fecha <span className="text-destructive">*</span>
            </Label>
            <Input
              id="session-date"
              type="date"
              value={form.session_date}
              onChange={set('session_date')}
              min={eventStartDate}
              max={eventEndDate}
              disabled={isProcessing}
            />
            {errors.session_date && <p className="text-xs text-destructive">{errors.session_date}</p>}
          </div>

          {/* Times */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="session-start-time">Hora de inicio</Label>
              <Input
                id="session-start-time"
                type="time"
                value={form.start_time}
                onChange={set('start_time')}
                disabled={isProcessing}
              />
              {errors.start_time && <p className="text-xs text-destructive">{errors.start_time}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="session-end-time">Hora de fin</Label>
              <Input
                id="session-end-time"
                type="time"
                value={form.end_time}
                onChange={set('end_time')}
                disabled={isProcessing}
              />
              {errors.end_time && <p className="text-xs text-destructive">{errors.end_time}</p>}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isProcessing}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isProcessing}>
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                {isEditing ? 'Guardando...' : 'Creando...'}
              </>
            ) : (
              isEditing ? 'Guardar cambios' : 'Crear sesión'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
